import { useEffect, useState } from "react";
import type { ApiResponseNormalizer, TableColumn } from "@/types/table";
import { fetchRecoveryFlatTable } from "@/analytics/recoveryDrillThrough";
import type { RecoveryFilters } from "@/analytics/recovery.api";
import { applySort, DEFAULT_SORT, type SortDir } from "@/lib/tableSort";
import { UtilisationDataTable } from "@/components/UtilisationDataTable";

type Props<T extends Record<string, unknown>> = {
  title: string;
  endpoint: string;
  filters: RecoveryFilters;
  normalizer: ApiResponseNormalizer<T>;
  emptyMessage?: string;
  sortable?: boolean;
};

type TableState<T extends Record<string, unknown>> = {
  data: T[];
  columns: TableColumn<T>[];
  loading: boolean;
  error: string | null;
};

export function RecoveryFlatTable<T extends Record<string, unknown>>({
  title,
  endpoint,
  filters,
  normalizer,
  emptyMessage,
  sortable = false,
}: Props<T>) {
  const [tableState, setTableState] = useState<TableState<T>>({
    data: [],
    columns: [],
    loading: true,
    error: null,
  });
  const [sort, setSort] = useState(DEFAULT_SORT);
  const handleSort = (key: string, direction: SortDir | null) => setSort(applySort(key, direction));

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      setTableState((s) => ({ ...s, loading: true, error: null }));
      try {
        const response = await fetchRecoveryFlatTable(
          endpoint,
          filters,
          controller.signal,
          sortable ? sort : undefined,
        );
        const normalized = normalizer(response);
        if (controller.signal.aborted) return;
        setTableState({
          data: normalized.rows as T[],
          columns: normalized.columns as TableColumn<T>[],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        setTableState({
          data: [],
          columns: [],
          loading: false,
          error: err instanceof Error ? err.message : "Unable to load data",
        });
      }
    }

    load();
    return () => controller.abort();
  }, [endpoint, filters, normalizer, sortable, sort]);

  return (
    <UtilisationDataTable
      title={title}
      data={tableState.data}
      columns={tableState.columns}
      loading={tableState.loading}
      error={tableState.error}
      emptyMessage={emptyMessage ?? "No records found"}
      sort={sortable ? sort : undefined}
      onSort={sortable ? handleSort : undefined}
    />
  );
}
